/**
 * WoW-style "spell activation overlay": a subtle flourish at the sides of the screen centre for
 * the one or two procs per class that most change the next press (Lava Surge → instant Lava
 * Burst, Sudden Death → Execute, …). Optional (settings: "Proc overlay"), on by default.
 *
 * Two fixed elements under the HUD root (not inside a scaled region), drawn with static CSS
 * gradients; only opacity / transform animate. Per frame: one pass over the player's auras with
 * an object lookup each, then class / custom-property writes only on change. No allocation.
 */
import type { DeepReadonly, Unit } from '@keystone/sim';
import { AURA } from '@keystone/content';
import { el } from '../dom';

export type OverlaySide = 'left' | 'right';

export interface OverlaySpec {
  side: OverlaySide;
  /** Flourish colour (CSS colour). */
  tint: string;
  /** Show only from this many stacks (Opening at its cap: "spend it before it overflows"). */
  atStacks?: number;
}

/**
 * The 1–2 most important procs per class (docs/design/rotation-rework.md §3). A web-side table:
 * the content contract has no overlay field, and ids that a build lacks simply never show.
 */
export const PROC_OVERLAYS: Readonly<Record<string, OverlaySpec>> = {
  // Stormcaller: the hard cast turns instant; the counter-driven transform.
  [AURA.lavaSurge]: { side: 'left', tint: '#ff8a3d' },
  [AURA.tempest]: { side: 'right', tint: '#7fc4ff' },
  // Blademaster: Execute unlocked; the Bladestorm release.
  [AURA.suddenDeath]: { side: 'left', tint: '#ff5a4a' },
  [AURA.ravagersFinaleReady]: { side: 'right', tint: '#ffb347' },
  // Warden: Shield Slam reset; Opening at its 2-stack cap.
  [AURA.shieldMomentum]: { side: 'left', tint: '#9fd0ff' },
  [AURA.opening]: { side: 'right', tint: '#f6e2a8', atStacks: 2 },
  // Lightbinder: Infusion (instant Flash or Greater Judgment); the free spender.
  [AURA.infusionOfLight]: { side: 'left', tint: '#ffe08a' },
  [AURA.divinePurpose]: { side: 'right', tint: '#fff3c4' },
};

/** Below this much time left the flourish fades toward its end (WoW: the overlay dims as it expires). */
export const OVERLAY_EXPIRING_MS = 2500;

/** What each side shows this frame (caller-owned, reused). */
export interface OverlayFrame {
  left: string | null;
  right: string | null;
  leftExpiring: boolean;
  rightExpiring: boolean;
}

export function emptyOverlayFrame(): OverlayFrame {
  return { left: null, right: null, leftExpiring: false, rightExpiring: false };
}

/** Pure: which overlay auras are up on `u` at sim time `now` (first matching aura per side wins). */
export function overlayFrame(u: DeepReadonly<Unit> | undefined, now: number, out: OverlayFrame, table: Readonly<Record<string, OverlaySpec>> = PROC_OVERLAYS): OverlayFrame {
  out.left = null;
  out.right = null;
  out.leftExpiring = false;
  out.rightExpiring = false;
  if (!u?.alive) return out;
  for (const inst of u.auras) {
    const spec = table[inst.auraId];
    if (!spec || (inst.stacks || 1) < (spec.atStacks ?? 1)) continue;
    const expiring = inst.expiresAt !== null && inst.expiresAt - now < OVERLAY_EXPIRING_MS;
    if (spec.side === 'left') {
      if (out.left === null) {
        out.left = inst.auraId;
        out.leftExpiring = expiring;
      }
    } else if (out.right === null) {
      out.right = inst.auraId;
      out.rightExpiring = expiring;
    }
  }
  return out;
}

class Side {
  readonly root: HTMLElement;
  private auraId: string | null = null;
  private expiring = false;

  constructor(parent: HTMLElement, side: OverlaySide) {
    this.root = el('div', `hk-sao-side ${side}`, parent);
    el('div', 'hk-sao-art', this.root);
  }

  set(auraId: string | null, expiring: boolean, table: Readonly<Record<string, OverlaySpec>>): void {
    if (auraId !== this.auraId) {
      // A different proc on the same side restarts the entrance (class off, then on next frame).
      const swap = auraId !== null && this.auraId !== null;
      this.auraId = auraId;
      if (auraId) this.root.style.setProperty('--sao', table[auraId]?.tint ?? '#f6e2a8');
      this.root.classList.toggle('on', !!auraId && !swap);
    } else if (auraId && !this.root.classList.contains('on')) {
      this.root.classList.add('on');
    }
    const exp = !!auraId && expiring;
    if (exp !== this.expiring) {
      this.expiring = exp;
      this.root.classList.toggle('expiring', exp);
    }
  }
}

export class ProcOverlay {
  readonly root: HTMLElement;
  private readonly left: Side;
  private readonly right: Side;
  private readonly frame = emptyOverlayFrame();
  private enabled = true;

  constructor(parent: HTMLElement, private readonly table: Readonly<Record<string, OverlaySpec>> = PROC_OVERLAYS) {
    this.root = el('div', 'hk-sao', parent);
    this.root.setAttribute('aria-hidden', 'true');
    this.left = new Side(this.root, 'left');
    this.right = new Side(this.root, 'right');
  }

  setEnabled(on: boolean): void {
    if (on === this.enabled) return;
    this.enabled = on;
    this.root.classList.toggle('hk-hidden', !on);
  }

  update(u: DeepReadonly<Unit> | undefined, now: number): void {
    const f = overlayFrame(this.enabled ? u : undefined, now, this.frame, this.table);
    this.left.set(f.left, f.leftExpiring, this.table);
    this.right.set(f.right, f.rightExpiring, this.table);
  }

  dispose(): void {
    this.root.remove();
  }
}
